import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Form as FormikForm, InputField, SelectField, TextField } from "components/common_components/Formik";
import * as Yup from "yup";
import Grid from "@material-ui/core/Grid";
import useDetailHandlers from "./useDetailHandlers";
import Button from "components/common_components/Button";
import { useSelector } from "react-redux";

function DetailForm({ invoiceId, detailData, resources, onCancel, onDetailSuccess, isUserCreating, setSelectedDetail }) {
    const { products } = resources;
    const { user } = useSelector((state) => state.Login);
    const { onSaveHandler } = useDetailHandlers({ onDetailSuccess, setSelectedDetail });

    const values = useMemo(() => {
        return {
            ...detailData,
            invoiceId,
        };
    }, [invoiceId, detailData]);

    const validationSchema = Yup.object({
        unitPrice: Yup.number().required("Required field"),
        quantity: Yup.number().required("Required field"),
        productId: Yup.string()
            .required("Required field")
            .oneOf(
                products.map((x) => x.id),
                "Not a valid service."
            ),
        notes: Yup.string().notRequired().nullable()
    });
    
    return (
        <div className="layout">
            <FormikForm
                validateOnChange={false}
                initialValues={values}
                validationSchema={validationSchema}
                onSubmit={onSaveHandler}
                onCancel={onCancel}
                showButtons={false}
                enableReinitialize
            >
                {(form) => (
                    <Grid container direction="column" spacing={2}>
                        <Grid item>
                            <SelectField fieldName="productId" label="Service *" options={products} valueKey="id" labelKey="name" />
                        </Grid>
                        <Grid item>
                            <InputField fieldName="quantity" label="Quantity *" numeric placeholder="1" />
                        </Grid>
                        <Grid item>
                            <InputField fieldName="unitPrice" label="Unit Price *" currency placeholder="0" />
                        </Grid>
                        <Grid item>
                            <TextField fieldName="notes" label="Notes" multiline minRows={3} />
                        </Grid>
                        <Grid item>
                            <Grid container direction="row-reverse" spacing={2}>
                                <Grid item>
                                    {user.isAdmin ? (
                                        <Button secondary type="submit" loading={form.isSubmitting}>
                                            {form.values.id ? "Update" : "Add"}
                                        </Button>
                                    ) : (
                                        <>
                                            {isUserCreating && (
                                                <Button secondary type="submit" loading={form.isSubmitting}>
                                                    {form.values.id ? "Update" : "Add"}
                                                </Button>
                                            )}
                                            {!isUserCreating && !form.values.id && (
                                                <Button secondary type="submit" loading={form.isSubmitting}>
                                                    Add
                                                </Button>
                                            )}
                                        </>
                                    )}
                                </Grid>
                                <Grid item>
                                    <Button
                                        secondary
                                        outline
                                        onClick={() => {
                                            form.resetForm();
                                            onCancel();
                                        }}
                                    >
                                        Reset
                                    </Button>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                )}
            </FormikForm>
        </div>
    );
}

DetailForm.propTypes = {
    detailData: PropTypes.shape({
        invoiceId: PropTypes.number,
        id: PropTypes.number,
        unitPrice: PropTypes.number,
        quantity: PropTypes.number,
        totalPrice: PropTypes.number,
        productId: PropTypes.string,
        notes: PropTypes.string,
    }),
    resources: PropTypes.shape({
        products: PropTypes.arrayOf(
            PropTypes.shape({
                id: PropTypes.string,
                name: PropTypes.string,
            })
        ),
    }),
    onCancel: PropTypes.func,
    onDetailSuccess: PropTypes.func,
    isUserCreating: PropTypes.bool,
    setSelectedDetail: PropTypes.func,
};

DetailForm.defaultProps = {
    detailData: {
        invoiceId: undefined,
        id: undefined,
        unitPrice: 0,
        quantity: 0,
        totalPrice: 0,
        productId: "",
        notes: "",
    },
    resources: {
        products: [],
    },
    onCancel: () => {},
    onDetailSuccess: (newId, payload) => true,
    isUserCreating: false,
    setSelectedDetail: (selectedDetail) => true,
};

export default DetailForm;
